import React, { useState } from 'react'
import { Box, Button, Popover, Stack, Typography } from '@mui/material'
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import PublicRoundedIcon from '@mui/icons-material/Public'

import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'

import type { FilterOption } from '../packagesFilterPanel/packagesFilterPanel'

export interface MonthTile extends FilterOption {
    monthLabel: string
    yearLabel: string
    /** No departures that month — shown, so the grid keeps its shape, but not selectable. */
    disabled: boolean
}

interface PackagesSearchBarProps {
    /** Small caption above each field, naming what it holds. */
    destinationLabel: string
    monthLabel: string
    destinationPlaceholder: string
    /** Selected cities as one line, or undefined when none are selected. */
    destinationValueLabel?: string
    anywhereLabel: string
    anywhereHint: string
    popularLabel: string
    cityOptions: FilterOption[]
    onToggleCity: (key: string) => void
    /** Browse the whole catalogue — the row leaves this page for the listing. */
    onAnywhere: () => void
    monthPlaceholder: string
    monthValueLabel?: string
    monthTitle: string
    monthHint: string
    monthTiles: MonthTile[]
    onToggleMonth: (key: string) => void
    onClearMonths: () => void
    doneLabel: string
    clearLabel: string
    packageCountLabel: (count: number) => string
    applyLabel: string
    onApply: () => void
    /** The draft matches what the grid already shows, so there is nothing to update. */
    applyDisabled: boolean
    isMobile: boolean
}

type OpenField = 'destination' | 'month' | null

const FIELD_SX = {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 2.5,
    py: 1.75,
    border: 'none',
    bgcolor: 'transparent',
    cursor: 'pointer',
    textAlign: 'start',
    font: 'inherit',
    color: 'inherit',
    '&:hover': { bgcolor: 'neutral.100' }
} as const

const PackagesSearchBar: React.FC<PackagesSearchBarProps> = ({
    destinationLabel,
    monthLabel,
    destinationPlaceholder,
    destinationValueLabel,
    anywhereLabel,
    anywhereHint,
    popularLabel,
    cityOptions,
    onToggleCity,
    onAnywhere,
    monthPlaceholder,
    monthValueLabel,
    monthTitle,
    monthHint,
    monthTiles,
    onToggleMonth,
    onClearMonths,
    doneLabel,
    clearLabel,
    packageCountLabel,
    applyLabel,
    onApply,
    applyDisabled,
    isMobile
}) => {
    const [openField, setOpenField] = useState<OpenField>(null)
    const [anchor, setAnchor] = useState<HTMLElement | null>(null)

    const open = (field: OpenField, element: HTMLElement) => {
        setAnchor(element)
        setOpenField(field)
    }
    const close = () => setOpenField(null)

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: 'stretch',
                    borderRadius: HOLIDAYS_RADIUS.SURFACE,
                    border: '1px solid',
                    borderColor: 'borders.default',
                    bgcolor: 'background.paper',
                    overflow: 'hidden'
                }}
            >
                <Box
                    component="button"
                    type="button"
                    onClick={event => open('destination', event.currentTarget)}
                    aria-expanded={openField === 'destination'}
                    sx={FIELD_SX}
                >
                    <LocationOnOutlinedIcon sx={{ fontSize: 22, color: 'textColors.subheading', flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.813rem', color: 'textColors.subheading', lineHeight: 1.4 }}>
                            {destinationLabel}
                        </Typography>
                        <Typography
                            noWrap
                            sx={{
                                fontSize: '1.125rem',
                                lineHeight: 1.4,
                                color: destinationValueLabel ? 'textColors.primary' : 'textColors.subheading',
                                fontWeight: destinationValueLabel ? 600 : 400
                            }}
                        >
                            {destinationValueLabel ?? destinationPlaceholder}
                        </Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        width: isMobile ? '100%' : '1px',
                        height: isMobile ? '1px' : 'auto',
                        bgcolor: 'borders.default',
                        flexShrink: 0
                    }}
                />

                <Box
                    component="button"
                    type="button"
                    onClick={event => open('month', event.currentTarget)}
                    aria-expanded={openField === 'month'}
                    sx={{ ...FIELD_SX, flex: isMobile ? 1 : 0.7 }}
                >
                    <CalendarTodayRoundedIcon sx={{ fontSize: 20, color: 'textColors.subheading', flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.813rem', color: 'textColors.subheading', lineHeight: 1.4 }}>
                            {monthLabel}
                        </Typography>
                        <Typography
                            noWrap
                            sx={{
                                fontSize: '1.125rem',
                                lineHeight: 1.4,
                                color: monthValueLabel ? 'textColors.primary' : 'textColors.subheading',
                                fontWeight: monthValueLabel ? 600 : 400
                            }}
                        >
                            {monthValueLabel ?? monthPlaceholder}
                        </Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        width: isMobile ? '100%' : '1px',
                        height: isMobile ? '1px' : 'auto',
                        bgcolor: 'borders.default',
                        flexShrink: 0
                    }}
                />

                {/* Sits inside the bar's rounded shell, so it reads as the end of the field row
                    rather than a button parked next to it. */}
                <Box sx={{ p: 0.75, flexShrink: 0 }}>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={onApply}
                        disabled={applyDisabled}
                        fullWidth={isMobile}
                        startIcon={<SearchRoundedIcon />}
                        sx={{
                            height: '100%',
                            minHeight: isMobile ? 48 : 56,
                            px: 4,
                            borderRadius: HOLIDAYS_RADIUS.NESTED,
                            fontSize: '1.063rem',
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            boxShadow: 'none',
                            '&:hover': { boxShadow: 'none' }
                        }}
                    >
                        {applyLabel}
                    </Button>
                </Box>
            </Box>

            <Popover
                open={openField === 'destination'}
                anchorEl={anchor}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            width: isMobile ? 'calc(100vw - 32px)' : 420,
                            maxHeight: 420,
                            borderRadius: HOLIDAYS_RADIUS.SURFACE,
                            border: '1px solid',
                            borderColor: 'borders.default'
                        }
                    }
                }}
            >
                <Box
                    component="button"
                    type="button"
                    onClick={() => {
                        close()
                        onAnywhere()
                    }}
                    sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2,
                        py: 1.5,
                        border: 'none',
                        bgcolor: 'transparent',
                        cursor: 'pointer',
                        textAlign: 'start',
                        font: 'inherit',
                        '&:hover': { bgcolor: 'neutral.100' }
                    }}
                >
                    <PublicRoundedIcon sx={{ fontSize: 22, color: 'primary.main' }} />
                    <Box>
                        <Typography sx={{ fontSize: '0.938rem', fontWeight: 700, color: 'textColors.primary' }}>
                            {anywhereLabel}
                        </Typography>
                        <Typography sx={{ fontSize: '0.813rem', color: 'textColors.subheading' }}>
                            {anywhereHint}
                        </Typography>
                    </Box>
                </Box>

                <Typography
                    sx={{
                        px: 2,
                        pt: 1,
                        pb: 0.5,
                        fontSize: '0.688rem',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: 'textColors.subheading'
                    }}
                >
                    {popularLabel}
                </Typography>

                {cityOptions.map(option => (
                    <Box
                        key={option.key}
                        component="button"
                        type="button"
                        // Picking a destination answers the field, so the list gets out of the
                        // way. The month picker keeps its Done button — months are chosen in
                        // groups, destinations one at a time.
                        onClick={() => {
                            onToggleCity(option.key)
                            close()
                        }}
                        aria-pressed={option.selected}
                        sx={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1.5,
                            px: 2,
                            py: 1.25,
                            border: 'none',
                            bgcolor: option.selected ? 'neutral.100' : 'transparent',
                            cursor: 'pointer',
                            textAlign: 'start',
                            font: 'inherit',
                            '&:hover': { bgcolor: 'neutral.100' }
                        }}
                    >
                        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
                            <LocationOnOutlinedIcon
                                sx={{
                                    fontSize: 20,
                                    flexShrink: 0,
                                    color: option.selected ? 'primary.main' : 'textColors.subheading'
                                }}
                            />
                            <Typography
                                noWrap
                                sx={{
                                    fontSize: '0.938rem',
                                    fontWeight: option.selected ? 700 : 500,
                                    color: 'textColors.primary'
                                }}
                            >
                                {option.label}
                            </Typography>
                        </Stack>
                        <Typography sx={{ fontSize: '0.813rem', color: 'textColors.subheading', flexShrink: 0 }}>
                            {packageCountLabel(option.count)}
                        </Typography>
                    </Box>
                ))}
            </Popover>

            <Popover
                open={openField === 'month'}
                anchorEl={anchor}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'left' : 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: isMobile ? 'left' : 'right' }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            p: 2,
                            width: isMobile ? 'calc(100vw - 32px)' : 560,
                            borderRadius: HOLIDAYS_RADIUS.SURFACE,
                            border: '1px solid',
                            borderColor: 'borders.default'
                        }
                    }
                }}
            >
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'baseline', justifyContent: 'space-between', mb: 1.5 }}
                >
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'textColors.primary' }}>
                        {monthTitle}
                    </Typography>
                    <Typography sx={{ fontSize: '0.813rem', color: 'textColors.subheading' }}>{monthHint}</Typography>
                </Stack>

                <Box
                    sx={{
                        display: 'grid',
                        // Three across on every screen: the window is nine months, so this is a
                        // square grid with no short final row.
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 1
                    }}
                >
                    {monthTiles.map(tile => (
                        <Box
                            key={tile.key}
                            component="button"
                            type="button"
                            disabled={tile.disabled}
                            onClick={() => onToggleMonth(tile.key)}
                            aria-pressed={tile.selected}
                            aria-label={tile.label}
                            sx={{
                                py: 1.25,
                                borderRadius: HOLIDAYS_RADIUS.NESTED,
                                border: '1px solid',
                                borderColor: tile.selected ? 'primary.main' : 'borders.default',
                                bgcolor: tile.selected ? 'primary.100' : 'background.paper',
                                cursor: tile.disabled ? 'default' : 'pointer',
                                opacity: tile.disabled ? 0.45 : 1,
                                font: 'inherit',
                                '&:hover': { borderColor: tile.disabled ? 'borders.default' : 'primary.main' }
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '0.938rem',
                                    fontWeight: tile.selected ? 700 : 600,
                                    color: 'textColors.primary'
                                }}
                            >
                                {tile.monthLabel}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: 'textColors.subheading' }}>
                                {tile.yearLabel}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                <Stack direction="row" sx={{ mt: 2, alignItems: 'center', justifyContent: 'space-between' }}>
                    <Button onClick={onClearMonths} sx={{ fontWeight: 700 }}>
                        {clearLabel}
                    </Button>
                    <Button variant="contained" onClick={close} sx={{ fontWeight: 700, px: 3 }}>
                        {doneLabel}
                    </Button>
                </Stack>
            </Popover>
        </>
    )
}

export { PackagesSearchBar }
export type { PackagesSearchBarProps }
export default PackagesSearchBar
